import React, { useState, useEffect, useCallback, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import { Row, Col, Spinner, Alert, Table, Modal, Badge, Button, Form, Card, Pagination, Nav } from "react-bootstrap";
import axios from "axios";
import { TranslatorContext } from "../../../../context/Translator";
import PageLayout from "../../../../layouts/PageLayout";
import PaymentApprovalButton from "../PaymentApprovalButton";

export default function ListerDetailPage() {
    const { id: listerId } = useParams();
    const { t } = useContext(TranslatorContext);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState("");
    const [activeTab, setActiveTab] = useState("summary");

    const [listerInfo, setListerInfo] = useState(null);
    const [summary, setSummary] = useState(null);
    const [products, setProducts] = useState([]);
    const [payments, setPayments] = useState([]);
    const [banks, setBanks] = useState([]);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
        from: 0,
        to: 0,
    });

    const [search, setSearch] = useState("");
    const [previewImage, setPreviewImage] = useState(null);

    // Modal & Form State
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [paymentForm, setPaymentForm] = useState({
        bank_id: "",
        amount: "",
        payment_date: new Date().toISOString().split("T")[0],
    });

    const resolveImageUrl = (path) => {
        if (!path) return "";
        if (path.startsWith("http://") || path.startsWith("https://")) {
            return path;
        }
        const baseUrl = process.env.REACT_APP_IMG_URL || "";
        return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };

    // Fetch Ledger Details
    const fetchListerDetails = useCallback(async (page = 1, searchQuery = "") => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/lister/ledger/${listerId}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        page: page,
                        search: searchQuery || undefined,
                    },
                }
            );

            if (response.data?.success && response.data?.data) {
                const resData = response.data.data;
                setListerInfo(resData.lister || null);
                setSummary(resData.summary || {});
                setProducts(resData.products || []);
                setPayments(resData.payments || []);
                if (resData.pagination) {
                    setPagination(resData.pagination);
                }
            }
        } catch (err) {
            console.error("Error fetching lister details:", err);
            setError(err.response?.data?.message || "Failed to fetch lister details.");
        } finally {
            setLoading(false);
        }
    }, [listerId]);

    // Fetch Banks List
    const fetchBanks = useCallback(async () => {
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/banks`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data?.success) {
                setBanks(response.data.data || response.data.banks || []);
            }
        } catch (err) {
            console.error("Error fetching banks:", err);
        }
    }, []);

    useEffect(() => {
        fetchListerDetails(currentPage, search);
        fetchBanks();
    }, [fetchListerDetails, currentPage, fetchBanks]);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.last_page) {
            setCurrentPage(page);
        }
    };

    // Add Payment Submit
    const handlePaymentSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        setSuccessMsg("");

        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/admin/lister/payment/${listerId}`,
                paymentForm,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data?.success) {
                setSuccessMsg("Payment added successfully.");
                setShowPaymentModal(false);
                setPaymentForm({
                    bank_id: "",
                    amount: "",
                    payment_date: new Date().toISOString().split("T")[0],
                });
                fetchListerDetails(currentPage, search);
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to add payment.");
        } finally {
            setSubmitting(false);
        }
    };

    const settlementType = (summary?.settlement_type || "").toLowerCase();
    const userPays = summary?.payment_direction === "receive" || settlementType === "receivable";
    const userReceives = summary?.payment_direction === "pay" || settlementType === "payable";
    const summaryLabels = userPays
        ? {
            total: "Total amount owed by Lister",
            settled: "Amount already paid by Lister",
            remaining: "Amount still owed by Lister",
            extra: "Extra amount paid by Lister",
        }
        : userReceives
            ? {
                total: "Total amount payable to Lister",
                settled: "Amount already paid to Lister",
                remaining: "Amount still payable to Lister",
                extra: "Extra amount paid to Lister",
            }
            : {
                total: "Total amount",
                settled: "Settled amount",
                remaining: "Remaining amount",
                extra: "Extra amount",
            };

    const formatAmount = (value) => {
        const amount = Number(value);
        return Number.isFinite(amount)
            ? amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : "0.00";
    };

    const getPaymentStatusVariant = (status) => {
        switch ((status || "").toLowerCase()) {
            case "approved":
                return "success";
            case "cancelled":
                return "danger";
            default:
                return "warning";
        }
    };
    const isCommissionBased = ["per_product", "percentage", "fixed_amount"].includes(listerInfo?.payment_type);

    return (
        <PageLayout>
            {/* Header Banner */}
            <Row className="mb-4">
                <Col xl={12}>
                    <div
                        className="mc-card p-4 rounded-4 shadow-sm border-0 d-flex justify-content-between align-items-center flex-wrap gap-3"
                        style={{ background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)", color: "#fff" }}
                    >
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <span className="material-icons text-primary fs-3">account_circle</span>
                                <h3 className="mc-breadcrumb-title m-0 text-white fw-bold">
                                    {listerInfo?.name || "Lister Profile"}
                                </h3>
                            </div>
                            <p className="text-white-50 small m-0 ms-4">
                                {t("Payment Structure")}: <strong className="text-white text-capitalize">{listerInfo?.payment_type || "N/A"}</strong> |
                                {t("Value")}: <strong className="text-white">{listerInfo?.payment_value ?? 0}</strong>
                            </p>
                            <ul className="mc-breadcrumb-list mt-2 ms-4">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link text-white-50">{t("Home")}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="/admin/lister" className="mc-breadcrumb-link text-white-50">{t("Listers")}</Link>
                                </li>
                                <li className="mc-breadcrumb-item text-primary fw-semibold">{t("Ledger Details")}</li>
                            </ul>
                        </div>

                        <div>
                            {summary?.who_adds_payment === "admin" ? (
                                <Button
                                    variant="primary"
                                    className="rounded-3 px-3 py-2 d-flex align-items-center gap-2 shadow-sm"
                                    onClick={() => setShowPaymentModal(true)}
                                >
                                    <span className="material-icons fs-5">add_card</span>
                                    {t("Add Payment")}
                                </Button>
                            ) : summary ? (
                                <span className="small text-muted">{t("Set payable or receivable on the profile to add payments.")}</span>
                            ) : null}
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Alert Messages */}
            {error && <Alert variant="danger" dismissible onClose={() => setError("")} className="mb-4 shadow-sm rounded-3">{error}</Alert>}
            {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg("")} className="mb-4 shadow-sm rounded-3">{successMsg}</Alert>}

            <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white border-bottom-0 pt-3">
                    <Nav variant="tabs" activeKey={activeTab} onSelect={(tab) => tab && setActiveTab(tab)}>
                        <Nav.Item><Nav.Link eventKey="summary">{t("Summary")}</Nav.Link></Nav.Item>
                        <Nav.Item><Nav.Link eventKey="products">{t("Checked Products")}</Nav.Link></Nav.Item>
                        <Nav.Item><Nav.Link eventKey="payments">{t("Payments")}</Nav.Link></Nav.Item>
                    </Nav>
                </Card.Header>
                {activeTab === "summary" && (
                    <Card.Body className="pt-4">
                        {summary ? (
                            <Row className="justify-content-center">
                                <Col lg={8} xl={7}>
                                    <Card className="border shadow-sm">
                                        <Card.Body className="p-0">
                                            <div className={`p-4 border-bottom ${userPays ? "bg-warning-subtle" : userReceives ? "bg-success-subtle" : "bg-light"}`}>
                                                <div className="small text-muted mb-1">
                                                    {t("Settlement summary")}
                                                    {summary.settlement_type && ` · ${t(summary.settlement_type)}`}
                                                </div>
                                                <h4 className={`fw-bold mb-1 ${userPays ? "text-warning-emphasis" : userReceives ? "text-success" : "text-dark"}`}>
                                                    {userPays
                                                        ? t("Lister owes the company")
                                                        : userReceives
                                                            ? t("Company owes the Lister")
                                                            : t("Lister account balance")}
                                                </h4>
                                                <div className="text-muted">
                                                    {summary.extra_amount > 0
                                                        ? t(userPays
                                                            ? "Lister has paid more than the amount owed to the company."
                                                            : userReceives
                                                                ? "The company has paid more than the amount owed to the Lister."
                                                                : "The account has an extra payment.")
                                                        : summary.remaining_amount > 0
                                                            ? t(userPays
                                                                ? "The Lister still owes this amount to the company."
                                                                : userReceives
                                                                    ? "The company still owes this amount to the Lister."
                                                                    : "This amount is still unsettled.")
                                                            : t("The account is fully settled.")}
                                                </div>
                                            </div>
                                            <div className="px-4">
                                                {[
                                                    { label: summaryLabels.total, value: summary.total_amount, emphasized: true },
                                                    { label: summaryLabels.settled, value: summary.settled_amount },
                                                    { label: summaryLabels.remaining, value: summary.remaining_amount, emphasized: true },
                                                    { label: summaryLabels.extra, value: summary.extra_amount },
                                                    { label: "Opening balance", value: summary.opening_balance },
                                                    { label: "Earned from checked products", value: summary.earned_amount },
                                                    { label: "Payment method", value: summary.payment_type ? t(summary.payment_type.replaceAll("_", " ")) : "-", text: true },
                                                    { label: "Payment value", value: summary.payment_value },
                                                    { label: "Checked products", value: summary.approved_products, count: true },
                                                ].map((item, index, items) => (
                                                    <div
                                                        key={item.label}
                                                        className={`d-flex justify-content-between align-items-center gap-3 py-3 ${index < items.length - 1 ? "border-bottom" : ""}`}
                                                    >
                                                        <span className={item.emphasized ? "fw-semibold text-dark" : "text-muted"}>
                                                            {t(item.label)}
                                                        </span>
                                                        <span className={`text-end ${item.emphasized ? "fw-bold text-dark fs-5" : "fw-semibold text-dark"}`}>
                                                            {item.count
                                                                ? item.value ?? 0
                                                                : item.text
                                                                    ? item.value
                                                                    : `PKR ${formatAmount(item.value)}`}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        ) : (
                            <div className="text-center text-muted py-4">{t("No summary data available.")}</div>
                        )}
                    </Card.Body>
                )}

                {activeTab === "products" && (
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                            <div>
                                <h5 className="fw-bold mb-1">{t("Checked Products")}</h5>
                                <small className="text-muted">{t("These products have passed checking and count toward the lister's earnings.")}</small>
                            </div>
                            <Badge bg="primary" className="px-3 py-2">{pagination.total || 0} {t("products")}</Badge>
                        </div>
                        {loading ? (
                            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                        ) : (
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>#</th>
                                            <th>{t("Product")}</th>
                                            <th>{t("Product Code")}</th>
                                            <th>{t("Status")}</th>
                                            <th>{t("Selling Price")}</th>
                                            <th className="text-end">{t("Checked Date")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {products.length ? products.map((product, index) => {
                                            const imageUrl = resolveImageUrl(product.card_img);
                                            const serial = (pagination.current_page - 1) * pagination.per_page + index + 1;
                                            return (
                                                <tr key={product.id}>
                                                    <td>{serial}</td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            {imageUrl ? (
                                                                <img
                                                                    src={imageUrl}
                                                                    alt={product.title || t("Product")}
                                                                    className="rounded border object-fit-cover"
                                                                    style={{ width: 44, height: 44, cursor: "pointer" }}
                                                                    onClick={() => setPreviewImage(imageUrl)}
                                                                    onError={(event) => { event.currentTarget.style.visibility = "hidden"; }}
                                                                />
                                                            ) : (
                                                                <div className="rounded bg-light border d-flex align-items-center justify-content-center text-muted small" style={{ width: 44, height: 44 }}>
                                                                    {t("No image")}
                                                                </div>
                                                            )}
                                                            <span className="fw-semibold">{product.title || "-"}</span>
                                                        </div>
                                                    </td>
                                                    <td>{product.product_code || "-"}</td>
                                                    <td><Badge bg="success">{t(product.status || "Checked")}</Badge></td>
                                                    <td>USD {formatAmount(product.sell_price_dollar)}</td>
                                                    <td className="text-end">{formatDate(product.approved_at)}</td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr><td colSpan={6} className="text-center text-muted py-5">{t("No checked products found.")}</td></tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                        {pagination.last_page > 1 && (
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 pt-3 mt-3 border-top">
                                <small className="text-muted">
                                    {t("Showing")} {pagination.from || 0} {t("to")} {pagination.to || 0} {t("of")} {pagination.total}
                                </small>
                                <Pagination size="sm" className="mb-0">
                                    <Pagination.Prev
                                        disabled={pagination.current_page <= 1}
                                        onClick={() => handlePageChange(pagination.current_page - 1)}
                                    />
                                    {[...Array(pagination.last_page)].map((_, index) => (
                                        <Pagination.Item
                                            key={index + 1}
                                            active={index + 1 === pagination.current_page}
                                            onClick={() => handlePageChange(index + 1)}
                                        >
                                            {index + 1}
                                        </Pagination.Item>
                                    ))}
                                    <Pagination.Next
                                        disabled={pagination.current_page >= pagination.last_page}
                                        onClick={() => handlePageChange(pagination.current_page + 1)}
                                    />
                                </Pagination>
                            </div>
                        )}
                    </Card.Body>
                )}

                {activeTab === "payments" && (
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                            <div>
                                <h5 className="fw-bold mb-1">{t("Payment history")}</h5>
                                <small className="text-muted">{t("Approved payments count toward the settlement. Pending and cancelled payments do not.")}</small>
                            </div>
                            <Badge bg="secondary" className="px-3 py-2">{payments.length} {t("payments")}</Badge>
                        </div>
                        {loading ? (
                            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                        ) : (
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>#</th>
                                            <th>{t("Payment Date")}</th>
                                            <th>{t("Status")}</th>
                                            <th>{t("Payment Type")}</th>
                                            <th>{t("Bank")}</th>
                                            <th>{t("Recorded By")}</th>
                                            <th>{t("Notes")}</th>
                                            <th className="text-end">{t("Amount")}</th>
                                            <th>{t("Action")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments.length ? payments.map((payment, index) => (
                                            <tr key={payment.id}>
                                                <td>{index + 1}</td>
                                                <td>{formatDate(payment.payment_date)}</td>
                                                <td>
                                                    <Badge bg={getPaymentStatusVariant(payment.status)}>
                                                        {t(payment.status || "pending")}
                                                    </Badge>
                                                </td>
                                                <td className="text-capitalize">{t(payment.payment_type || "-")}</td>
                                                <td>{payment.bank?.name || "-"}</td>
                                                <td>{payment.creator?.name || "-"}</td>
                                                <td>{payment.notes || "-"}</td>
                                                <td className="text-end fw-semibold">PKR {formatAmount(payment.amount)}</td>
                                                <td>
                                                    <PaymentApprovalButton
                                                        payment={payment}
                                                        onApproved={() => fetchListerDetails(currentPage, search)}
                                                        t={t}
                                                    />
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan={9} className="text-center text-muted py-5">{t("No payment history found.")}</td></tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Card.Body>
                )}
            </Card>

            {false && <Row className="g-4">
                {/* Listings Table */}
                <Col lg={6}>
                    <div className="mc-card p-4 rounded-4 shadow-sm border-0 bg-white h-100 d-flex flex-column justify-content-between">
                        <div>
                            <div className="d-flex justify-content-between align-items-center flex-wrap mb-4 gap-3 border-bottom pb-3">
                                <h5 className="fw-bold text-dark m-0 d-flex align-items-center gap-2">
                                    <span className="material-icons text-primary fs-5">inventory_2</span>
                                    {t("Listings")} ({pagination.total})
                                </h5>
                            </div>

                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table hover className="align-middle border-0 m-0">
                                        <thead className="bg-light">
                                            <tr className="text-uppercase small text-muted">
                                                <th className="py-3 px-2 border-0" style={{ width: "40px" }}>#</th>
                                                <th className="py-3 px-2 border-0">{t("Listing Info")}</th>
                                                <th className="py-3 px-2 border-0">{t("Status")}</th>
                                                <th className="py-3 px-2 border-0 text-end">{t("Verified Date")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {products.length > 0 ? (
                                                products.map((product, idx) => {
                                                    const fullImgUrl = resolveImageUrl(product.card_img);
                                                    const serialNum = (pagination.current_page - 1) * pagination.per_page + idx + 1;

                                                    return (
                                                        <tr key={product.id || idx} className="border-bottom">
                                                            <td className="py-3 px-2 text-muted fw-semibold">{serialNum}</td>
                                                            <td className="py-3 px-2">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    {fullImgUrl ? (
                                                                        <img
                                                                            src={fullImgUrl}
                                                                            alt={product.title}
                                                                            className="rounded-2 border object-fit-cover flex-shrink-0"
                                                                            style={{ width: "38px", height: "38px", cursor: "pointer" }}
                                                                            onClick={() => setPreviewImage(fullImgUrl)}
                                                                            onError={(e) => { e.target.style.display = "none"; }}
                                                                        />
                                                                    ) : (
                                                                        <div className="rounded-2 bg-light d-flex align-items-center justify-content-center border text-muted flex-shrink-0" style={{ width: "38px", height: "38px", fontSize: "10px" }}>
                                                                            No Logo
                                                                        </div>
                                                                    )}
                                                                    <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                                                                        <span className="fw-semibold text-dark text-truncate" style={{ maxWidth: "150px" }}>
                                                                            {product.title || "N/A"}
                                                                        </span>
                                                                        <small className="text-primary fw-medium" style={{ fontSize: "11px" }}>
                                                                            #{product.product_code || "N/A"}
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="py-3 px-2">
                                                                <Badge bg="success" className="px-2 py-1 font-semibold">
                                                                    {product.status || "Verified"}
                                                                </Badge>
                                                            </td>
                                                            <td className="py-3 px-2 text-end text-muted small">{formatDate(product.checked_at)}</td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan={4} className="text-center py-5 text-muted">
                                                        {t("No listings found.")}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </div>

                        {/* Pagination Footer */}
                        {pagination.last_page > 1 && (
                            <div className="d-flex justify-content-between align-items-center flex-wrap pt-3 mt-3 border-top">
                                <small className="text-muted">
                                    Showing {pagination.from} to {pagination.to} of {pagination.total} entries
                                </small>
                                <Pagination className="m-0 pagination-sm">
                                    <Pagination.Prev
                                        disabled={pagination.current_page === 1}
                                        onClick={() => handlePageChange(pagination.current_page - 1)}
                                    />
                                    {[...Array(pagination.last_page)].map((_, i) => (
                                        <Pagination.Item
                                            key={i + 1}
                                            active={i + 1 === pagination.current_page}
                                            onClick={() => handlePageChange(i + 1)}
                                        >
                                            {i + 1}
                                        </Pagination.Item>
                                    ))}
                                    <Pagination.Next
                                        disabled={pagination.current_page === pagination.last_page}
                                        onClick={() => handlePageChange(pagination.current_page + 1)}
                                    />
                                </Pagination>
                            </div>
                        )}
                    </div>
                </Col>

                {/* Payment History Table */}
                <Col lg={6}>
                    <div className="mc-card p-4 rounded-4 shadow-sm border-0 bg-white h-100 d-flex flex-column justify-content-between">
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                                <h5 className="fw-bold text-dark m-0 d-flex align-items-center gap-2">
                                    <span className="material-icons text-success fs-5">payments</span>
                                    {isCommissionBased ? t("Commission Settlement History") : t("Salary Payout History")} ({payments.length})
                                </h5>
                            </div>

                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table hover className="align-middle border-0 m-0">
                                        <thead className="bg-light">
                                            <tr className="text-uppercase small text-muted">
                                                <th className="py-3 px-2 border-0" style={{ width: "40px" }}>#</th>
                                                <th className="py-3 px-2 border-0">{t("Amount Paid")}</th>
                                                <th className="py-3 px-2 border-0">{t("Bank Channel")}</th>
                                                <th className="py-3 px-2 border-0 text-end">{t("Payment Date")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {payments.length > 0 ? (
                                                payments.map((payment, idx) => (
                                                    <tr key={payment.id || idx} className="border-bottom">
                                                        <td className="py-3 px-2 fw-semibold text-dark">{idx + 1}</td>
                                                        <td className="py-3 px-2 fw-bold text-success">
                                                            {Number(payment.amount).toFixed(2)}
                                                        </td>
                                                        <td className="py-3 px-2 text-muted small">{payment.bank || "N/A"}</td>
                                                        <td className="py-3 px-2 text-end text-muted small">{formatDate(payment.date)}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={4} className="text-center py-5 text-muted">
                                                        {isCommissionBased ? t("No payment settlement records found.") : t("No salary payout records found.")}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>}

            {/* Add Payment Modal */}
            <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold text-dark fs-5">{t("Add Commission Payment")}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handlePaymentSubmit}>
                    <Modal.Body className="p-4">
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold text-dark">{t("Select Bank Account")} <span className="text-danger">*</span></Form.Label>
                            <Form.Select
                                required
                                value={paymentForm.bank_id}
                                onChange={(e) => setPaymentForm({ ...paymentForm, bank_id: e.target.value })}
                            >
                                <option value="">{t("Choose Bank...")}</option>
                                {banks.map((bank) => (
                                    <option key={bank.id} value={bank.id}>
                                        {bank.name || bank.bank_name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold text-dark">{t("Payment Amount")} <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                min="0.01"
                                placeholder="0.00"
                                required
                                value={paymentForm.amount}
                                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold text-dark">{t("Payment Date")}</Form.Label>
                            <Form.Control
                                type="date"
                                value={paymentForm.payment_date}
                                onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0">
                        <Button variant="light" onClick={() => setShowPaymentModal(false)}>
                            {t("Cancel")}
                        </Button>
                        <Button variant="primary" type="submit" disabled={submitting}>
                            {submitting ? <Spinner size="sm" animation="border" /> : t("Submit Payment")}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Image Preview Modal */}
            <Modal show={!!previewImage} onHide={() => setPreviewImage(null)} centered size="md">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fs-6 text-muted">{t("Listing Preview")}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center p-3">
                    {previewImage && (
                        <img
                            src={previewImage}
                            alt="Listing Preview"
                            className="img-fluid rounded-3 shadow"
                            style={{ maxHeight: "70vh", objectFit: "contain" }}
                        />
                    )}
                </Modal.Body>
            </Modal>
        </PageLayout>
    );
}